import type { ChangeEvent } from 'react';
import { useFormik } from 'formik';
import { capitalize } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { when } from 'util/support';
import type { PaymentMethod as PaymentMethodType } from 'features/paymentMethods/types';
import { CustomAvatar } from 'lib/custom-avatar';
import LabeledSwitch from 'ui/atoms/LabeledSwitch';
import { usePaymentMethodsContext } from '../PaymentMethodsProvider';

type Props =
    & PaymentMethodType
    & {
        readonly form: ReturnType<typeof useFormik>;
    };

const PaymentMethod = ({
    form,
    ...restProps
}: Props) => {
    const { tag, coreId, merchantPayoutIncrementTaxes } = restProps;

    const {
        values,
        setFieldValue,
        setFieldTouched
    } = form;

    const {
        paymentMethod,
        comparePaymentMethods,
        togglePaymentMethod
    } = usePaymentMethodsContext();

    const handleChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setFieldValue(tag, [coreId, checked]);
        setTimeout(() => setFieldTouched(tag, true));
    };

    return (
        <Card>
            <CardContent
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto auto',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                <CustomAvatar
                    name={tag}
                    alt={tag}
                />
                <Typography
                    variant='h5'
                >
                    {capitalize(tag)}
                </Typography>
                <Tooltip
                    arrow
                    placement='top'
                    title={when(
                        merchantPayoutIncrementTaxes.length,
                        'Click to view fees & pricing',
                        'No fees & pricing available 🖕'
                    )}
                >
                    <Box
                        component='span'
                    >
                        <Chip
                            {...when(
                                comparePaymentMethods(restProps, paymentMethod),
                                {
                                    color: 'primary'
                                },
                                {
                                    variant: 'outlined'
                                }
                            )}
                            clickable
                            label='Fees & Pricing'
                            disabled={!merchantPayoutIncrementTaxes.length}
                            onClick={() => togglePaymentMethod(restProps)}
                        />
                    </Box>
                </Tooltip>
                <LabeledSwitch
                    RootProps={{
                        sx: {
                            mr: -4
                        }
                    }}
                    checked={values[tag]?.[1]}
                    label={(
                        <Box
                            sx={{
                                fontWeight: 600
                            }}
                        >
                            {when(
                                values[tag]?.[1],
                                'Enabled',
                                <Box
                                    sx={{
                                        color: 'text.disabled'
                                    }}
                                >
                                    Disabled
                                </Box>
                            )}
                        </Box>
                    )}
                    onChange={handleChange}
                    FormControlLabelProps={{
                        sx: {
                            ml: 2,
                            flexDirection: 'row-reverse'
                        }
                    }}
                />
            </CardContent>
        </Card>
    );
};

export default PaymentMethod;
